import { type Component, createEffect, createMemo, createSignal, mergeProps } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import { AccordionContext, type AccordionContextType } from '~/contexts/AccordionContext'
import type { AccordionGroupPropsType } from '~/types/Accordion'

const AccordionGroup: Component<AccordionGroupPropsType> = props => {
  const merged = mergeProps(
    {
      multiple: false,
      divider: true,
      defaultValue: props.multiple ? [] : undefined
    },
    props
  )

  const normalizeInitialValue = () => {
    if (merged.defaultValue === undefined) return []
    return Array.isArray(merged.defaultValue) ? merged.defaultValue : [merged.defaultValue]
  }

  const [openValues, setOpenValues] = createSignal<string[]>(normalizeInitialValue())

  const currentValues = () => {
    if (merged.value !== undefined) {
      return Array.isArray(merged.value) ? merged.value : merged.value ? [merged.value] : []
    }

    return openValues()
  }

  const updateValues = (newValues: string[]) => {
    if (merged.value === undefined) {
      setOpenValues(newValues)
    }

    if (merged.onValueChange) {
      if (merged.multiple) {
        merged.onValueChange(newValues.length > 0 ? newValues : undefined)
      } else {
        merged.onValueChange(newValues.length > 0 ? newValues[0] : undefined)
      }
    }
  }

  const isOpen = (value: string): boolean => {
    return currentValues().includes(value)
  }

  const toggle = (value: string) => {
    const current = currentValues()

    if (merged.multiple) {
      if (current.includes(value)) {
        updateValues(current.filter(v => v !== value))
      } else {
        updateValues([...current, value])
      }
    } else {
      if (current.includes(value)) {
        updateValues([])
      } else {
        updateValues([value])
      }
    }
  }

  createEffect(() => {
    if (merged.value === undefined) {
      setOpenValues(normalizeInitialValue())
    }
  })

  createEffect(() => {
    if (merged.value !== undefined) {
      /* empty */
    }
  })

  const contextValue: AccordionContextType = {
    multiple: () => merged.multiple,
    isOpen,
    toggle
  }

  const groupClasses = createMemo(() =>
    twMerge(
      'accordion-group',

      merged.divider && '[&>:not(:last-child)]:border-b [&>:not(:last-child)]:border-border',
      merged.class
    )
  )

  return (
    <AccordionContext.Provider value={contextValue}>
      <div class={groupClasses()} classList={merged.classList}>
        {props.children}
      </div>
    </AccordionContext.Provider>
  )
}

export default AccordionGroup
